import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ErrorResponse } from 'react-router-dom';

import {
  CodePromoResponse,
  deleteCodePromoRestaurantRequest,
  fetchCodePromoRestaurantRequest,
  fetchCodePromosRestaurantRequest,
  patchCodePromoRestaurantRequest,
  updateOrCreateCodePromoRestaurantRequest
} from '../lib/codePromo';
import { UseQueryConfig } from '../lib/types';

export const useFetchCodePromoMutation = ({ onSuccess }: { onSuccess?: (response: CodePromoResponse) => void }) => {
  return useMutation<CodePromoResponse, ErrorResponse, string>((codePromoId) => fetchCodePromoRestaurantRequest(codePromoId), {
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data);
      }
    }
  });
};

export const useFetchCodePromosQuery = (restaurantId: string, config?: UseQueryConfig<CodePromoResponse[]>) => {
  return useQuery<CodePromoResponse[], ErrorResponse>({
    queryKey: ['coupons', restaurantId],
    queryFn: () => fetchCodePromosRestaurantRequest(restaurantId),
    ...config
  });
};

export const useUpdateOrCreateCodePromoMutation = ({ onSuccess }: { onSuccess?: (response: CodePromoResponse) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<CodePromoResponse, ErrorResponse, { codePromo: CodePromoResponse; restaurantId: string }>(
    ({ codePromo }) => updateOrCreateCodePromoRestaurantRequest(codePromo),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['coupons', variables.restaurantId]
        });
        onSuccess?.(data);
      }
    }
  );
};

export const usePatchCodePromoMutation = ({ onSuccess }: { onSuccess?: (response: CodePromoResponse) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<CodePromoResponse, ErrorResponse, { couponId: string; codePromo: Partial<CodePromoResponse>; restaurantId: string }>(
    ({ couponId, codePromo }) => patchCodePromoRestaurantRequest(couponId, codePromo),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['coupons', variables.restaurantId]
        });
        onSuccess?.(data);
      }
    }
  );
};

export const useDeleteCodePromoMutation = ({ onSuccess }: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  return useMutation<void, ErrorResponse, string>((codePromoId) => deleteCodePromoRestaurantRequest(codePromoId), {
    onSuccess: () => {
      if (onSuccess) {
        queryClient.invalidateQueries({ queryKey: ['coupons'] });
        onSuccess();
      }
    }
  });
};
